import { capitalize } from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { PencilIcon } from '../../components/icons';
import { CustomInput, ToggleButton, UseFormDropDown } from '../../components/common';
import Modal from '../../components/common/modal';
import { onBlur } from '../../components/common/useForm';
import AlertBox from '../../components/route-leaving-prompt/alert-box';
import {
  ExternalProviderPlan,
  useAddExternalProviderPlan,
  useUpdateExternalProviderPlan,
} from '../../hooks/useExternalProviderPlans';

interface ExternalProviderPlanModalProps {
  plan: ExternalProviderPlan;
}

const ExternalProviderPlanModal: React.FC<ExternalProviderPlanModalProps> = ({ plan }) => {
  const isEdit = plan.id !== undefined;
  const { addExternalProviderPlan } = useAddExternalProviderPlan();
  const { updateExternalProviderPlan } = useUpdateExternalProviderPlan(plan.id ?? 0);
  const [modal, setModal] = useState<boolean>(false);
  const [alertModal, setAlertModal] = useState<boolean>(false);

  const providers = [
    { name: 'DialCare', value: 'dialcare' },
    { name: 'MeMD', value: 'me_md' },
    { name: 'Recuro', value: 'recuro' },
    { name: 'Opyn', value: 'opyn' },
    { name: 'Revive', value: 'revive' },
  ];
  const providerPlans = ['counseling', 'primary', 'urgent', 'crisis', 'crisis_lite', 'rx', 'pediatric'];

  const defaultValues = useMemo(
    () => ({
      code: plan?.code || '',
      display_name: plan?.display_name || '',
      name: plan?.name || '',
      provider: plan?.provider || '',
      provider_types: plan?.provider_types || ([] as string[]),
      recuro_metatag3: plan?.recuro_metatag3 || '',
    }),
    [plan],
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isDirty },
  } = useForm({ defaultValues });

  useEffect(() => {
    if (isEdit) {
      reset(defaultValues);
    }
  }, [defaultValues, isEdit, reset]);

  const handleSetStatus = (isChecked: boolean, type: string, field: any) => {
    field.onChange(isChecked ? [...field.value, type] : field.value.filter((value: string) => value !== type));
  };

  const handleSave = (data: ExternalProviderPlan) => {
    if (isEdit) {
      updateExternalProviderPlan.mutate(data);
    } else {
      addExternalProviderPlan.mutate(data);
      reset();
    }
    setModal(false);
    setAlertModal(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setModal(true);
        }}
        className={
          isEdit
            ? 'p-1 text-gray-600 hover:text-gray-900'
            : 'bg-blue-primary shrink-0 rounded-sm px-2 py-2 text-center text-sm text-white'
        }
        aria-label={isEdit ? `Edit ${plan.name}` : 'Add Plan'}
        data-testid={isEdit ? `plus-care-edit-button-${plan.id}` : 'plus-care-add-button'}
      >
        {isEdit ? <PencilIcon /> : 'Add Plan +'}
      </button>
      <Modal
        isModel={modal}
        InnerComponent={
          <div
            onClick={(event) => {
              event.stopPropagation();
            }}
            className="inline-block transform overflow-hidden bg-white align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:align-middle"
          >
            <form onSubmit={handleSubmit(handleSave)} id="external-provider-plan-form">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="flex justify-start">
                  <h3 className="blue-primary py-1 text-2xl font-normal">{isEdit ? 'Edit Plan' : 'Add Plan'}</h3>
                </div>
                <div className="mr-5 grid grid-cols-2 gap-3 gap-y-5">
                  <CustomInput
                    inputType="text"
                    Label="Name"
                    register={{
                      ...register('name', {
                        required: 'Please provide a name',
                        onBlur: (e: any) => onBlur(e, 'name', setValue),
                      }),
                    }}
                    Error={!!errors?.name?.message?.length}
                    ErrorMessage={errors.name?.message}
                    dataTestId="name"
                  />
                  <CustomInput
                    inputType="text"
                    Label="Code"
                    register={{
                      ...register('code', {
                        onBlur: (e: any) => onBlur(e, 'code', setValue),
                      }),
                    }}
                    Error={!!errors?.code?.message?.length}
                    ErrorMessage={errors.code?.message}
                    dataTestId="code"
                  />
                  <CustomInput
                    inputType="text"
                    Label="Display Name"
                    register={{
                      ...register('display_name', {
                        onBlur: (e: any) => onBlur(e, 'display_name', setValue),
                      }),
                    }}
                    Error={!!errors?.display_name?.message?.length}
                    ErrorMessage={errors.display_name?.message}
                    dataTestId="display-name"
                  />
                  <div className="flex flex-col">
                    <UseFormDropDown label={'Provider'}>
                      <select
                        className="select-box w-full focus:ring-transparent"
                        {...register('provider', { required: 'Provider is required' })}
                      >
                        {providers.map((provider) => (
                          <option key={provider.value} value={provider.value}>
                            {provider.name}
                          </option>
                        ))}
                      </select>
                    </UseFormDropDown>
                    {errors.provider && (
                      <p className="text-red mb-2 block pt-2 text-xs font-medium leading-4">
                        {errors.provider.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <div className="text-gray-dark mb-2 block text-left leading-4">Provider Type:</div>
                    {providerPlans.map((type, index) => (
                      <div key={type} className="text-gray-dark flex text-base">
                        <Controller
                          name="provider_types"
                          control={control}
                          rules={index === 0 ? { required: 'At least one provider type is required' } : {}}
                          render={({ field }) => (
                            <ToggleButton
                              setStatus={(isChecked) => handleSetStatus(isChecked, type, field)}
                              status={field.value.includes(type)}
                              id={type}
                            />
                          )}
                        />
                        <span>{capitalize(type)}</span>
                      </div>
                    ))}
                    {errors.provider_types && (
                      <p className="text-red mb-2 block pt-2 text-xs font-medium leading-4">
                        {errors.provider_types.message}
                      </p>
                    )}
                  </div>
                  <CustomInput
                    inputType="text"
                    Label="Recuro Metatag3"
                    register={{
                      ...register('recuro_metatag3', {
                        onBlur: (e: any) => onBlur(e, 'recuro_metatag3', setValue),
                      }),
                    }}
                    Error={!!errors?.recuro_metatag3?.message?.length}
                    ErrorMessage={errors.recuro_metatag3?.message}
                    dataTestId="recuro-metatag3"
                  />
                </div>
              </div>
              <div className="items-center gap-2 bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  className={
                    'h-8 w-16 rounded-sm px-2 text-center text-sm text-white ' +
                    (isDirty ? 'bg-blue-primary' : 'bg-gray-300')
                  }
                  type="submit"
                  form="external-provider-plan-form"
                  disabled={!isDirty}
                  data-testid="plus-care-edit-save-button"
                >
                  Save
                </button>
                <button
                  type="button"
                  className="inline-flex h-8 w-16 items-center rounded-sm border bg-white px-2 text-center text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={() => {
                    if (isDirty) {
                      setAlertModal(true);
                    } else {
                      setModal(false);
                    }
                  }}
                  data-testid="plus-care-edit-cancel-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        }
      />
      <AlertBox
        visible={alertModal}
        handlePrompt={() => {
          reset(defaultValues);
          setModal(false);
          setAlertModal(false);
        }}
        closeModal={() => {
          setAlertModal(false);
        }}
        confirmButtonText={'Discard Changes'}
        titleText={'Alert'}
        contentText={'You have unsaved changes. If you leave this screen without saving, your changes will be lost.'}
        cancelButtonText={'Cancel'}
      />
    </>
  );
};

export default ExternalProviderPlanModal;
